import { EVENT_MEDIA_OPTIONS, type EventMediaType } from '@/constants/sponsor'
import { createMarkup } from '@/constants/event'
import { DndContext } from '@dnd-kit/core'
import {
  useBannerDragDrop,
  type AllElementPositions,
} from '@/hooks/useBannerDragDrop'
import { DraggableText } from '../DraggableText'

interface BannerData {
  adName?: string
  adDescription?: string
  linkText?: string
  redirectionLink?: string
  sponsorLogoUrl?: string | null
  eventImageUrl?: string | null
  eventMediaType?: EventMediaType
  showTitle?: boolean
  showDescription?: boolean
  namePosition?: { x: number; y: number }
  descriptionPosition?: { x: number; y: number }
  sponsorLogoPosition?: { x: number; y: number }
  linkPosition?: { x: number; y: number }
}

interface HomeInFeedNativeCardBannerProps {
  data: BannerData
  editable?: boolean
  onPositionChange?: (positions: AllElementPositions) => void
}

const HomeInFeedNativeCardBanner = ({
  data,
  editable = false,
  onPositionChange,
}: HomeInFeedNativeCardBannerProps) => {
  const {
    adName,
    adDescription,
    linkText,
    sponsorLogoUrl,
    eventImageUrl,
    eventMediaType = EVENT_MEDIA_OPTIONS.IMAGE,
    redirectionLink,
    showTitle = true,
    showDescription = true,
    namePosition: initialNamePosition,
    descriptionPosition: initialDescriptionPosition,
    sponsorLogoPosition: initialSponsorLogoPosition,
    linkPosition: initialLinkPosition,
  } = data

  const {
    containerRef,
    namePosition,
    descriptionPosition,
    sponsorLogoPosition,
    linkPosition,
    handleDragEnd,
  } = useBannerDragDrop({
    initialNamePosition,
    initialDescriptionPosition,
    initialSponsorLogoPosition,
    initialLinkPosition,
    defaultNamePosition: { x: 10, y: 24 }, // ~36px/360px = 10%, ~120px/500px = 24%
    defaultDescriptionPosition: { x: 10, y: 64 }, // ~36px/360px = 10%, ~320px/500px = 64%
    defaultSponsorLogoPosition: { x: 10, y: 4 }, // ~36px/360px = 10%, ~20px/500px = 4%
    defaultLinkPosition: { x: 10, y: 88 }, // ~36px/360px = 10%, ~440px/500px = 88%
    onPositionChange,
  })

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <div
        ref={containerRef}
        className="relative w-[360px] h-[500px] sm:min-h-[450px] lg:min-h-[500px] rounded-2xl overflow-hidden bg-gradient-to-br from-red-500 via-orange-500 to-yellow-400 pointer-none mx-auto"
      >
        {/* Background Image/Video */}
        {eventImageUrl ? (
          eventMediaType === EVENT_MEDIA_OPTIONS.VIDEO ? (
            <video
              src={eventImageUrl}
              className="absolute inset-0 object-cover w-full h-full scale-125 mb-6 object-[70%_0%]"
              autoPlay
              loop
              muted
              playsInline
              aria-label={
                adName
                  ? `${adName} banner video background`
                  : 'Advertisement banner video background'
              }
            />
          ) : (
            <img
              src={eventImageUrl}
              alt={
                adName
                  ? `${adName} banner background`
                  : 'Advertisement banner background'
              }
              width={400}
              height={500}
              className="absolute inset-0 object-cover w-full h-full scale-125 mb-6 object-[70%_0%]"
              draggable={false}
            />
          )
        ) : (
          <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-ss-neutral-200 to-ss-neutral-300" />
        )}

        {/* Custom Gradient Overlay using Tailwind */}
        <div
          className="absolute inset-0 pointer-events-none [background:linear-gradient(180deg,rgba(0,0,0,0)_32.21%,rgba(0,0,0,0.8)_87.02%)]"
          aria-hidden="true"
        />
        {/* Red Bull logo area - simplified */}

        {sponsorLogoUrl && (
          <DraggableText
            position={sponsorLogoPosition}
            isEditable={editable}
            id="logo"
          >
            <img
              src={sponsorLogoUrl}
              alt="redbull logo"
              width={100}
              height={20}
              className="object-cover object-center mb-6"
            />
          </DraggableText>
        )}

        {showTitle && adName && (
          <DraggableText
            position={namePosition}
            isEditable={editable}
            id="title"
          >
            <p
              className="font-bold italic wrap-anywhere"
              dangerouslySetInnerHTML={createMarkup(adName || '')}
            ></p>
          </DraggableText>
        )}

        {/* Bottom text */}
        {showDescription && adDescription && (
          <DraggableText
            position={descriptionPosition}
            isEditable={editable}
            id="description"
          >
            <p
              className="font-bold wrap-anywhere"
              dangerouslySetInnerHTML={createMarkup(adDescription || '')}
            />
          </DraggableText>
        )}

        {/* CTA Button */}
        {linkText && (
          <DraggableText
            position={linkPosition}
            isEditable={editable}
            id="button"
          >
            <a
              target="_blank"
              className="bg-white text-black font-bold border-0 rounded-full px-4 sm:px-5 py-2 sm:py-2.5 hover:bg-white/90 text-sm sm:text-base h-9"
              href={redirectionLink}
              aria-label={
                linkText
                  ? `${linkText} - advertisement call to action`
                  : 'Advertisement call to action'
              }
              rel="noopener noreferrer"
            >
              {linkText}
            </a>
          </DraggableText>
        )}
      </div>
    </DndContext>
  )
}

export default HomeInFeedNativeCardBanner
